import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  BottomNavigation, 
  BottomNavigationAction, 
  Paper,
  Container,
  useTheme,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  GlobalStyles
} from '@mui/material';
import { 
  Home as HomeIcon, 
  Receipt as ReceiptIcon, 
  PieChart as PieChartIcon, 
  Settings as SettingsIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Layout = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Prevent bounce effect on mobile
  useEffect(() => {
    // Create a style element
    const style = document.createElement('style');
    // Add CSS to prevent bounce
    style.textContent = `
      html, body {
        overscroll-behavior-y: none;
        overflow: hidden;
        position: relative;
        height: 100%;
        width: 100%;
      }
    `;
    // Append to head
    document.head.appendChild(style);
    
    // Clean up
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  // Determine the active navigation item based on the current path
  const getActiveNavValue = () => {
    const path = location.pathname;
    if (path === '/' || path === '/home') return 0;
    if (path === '/transactions') return 1;
    if (path === '/breakdown') return 2;
    if (path === '/settings') return 3;
    return 0; // Default to home
  };
  
  // Handle navigation changes
  const handleNavChange = (event, newValue) => {
    switch (newValue) {
      case 0:
        navigate('/');
        break;
      case 1:
        navigate('/transactions');
        break;
      case 2:
        navigate('/breakdown');
        break;
      case 3:
        navigate('/settings');
        break;
      default:
        navigate('/');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      // PrivateRoute will handle the redirect
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh', 
      width: '100vw',
      bgcolor: theme.palette.background.default,
      position: 'relative',
      overflow: 'hidden',
      touchAction: 'manipulation', // Improves touch handling
      WebkitOverflowScrolling: 'touch' // Better scrolling on iOS
    }}>
      {/* Add this App Bar */}
      <AppBar 
        position="fixed" 
        color="primary" 
        elevation={0} 
        sx={{ 
          zIndex: 1200,
          width: '100%',
          top: 0,
          left: 0,
          right: 0,
          position: 'fixed',
          transform: 'translateZ(0)', // Hardware acceleration
          willChange: 'transform', // Hint to browser for optimization
          backdropFilter: 'blur(8px)' // Ensures visibility even with content behind
        }}
      >
        <Toolbar sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'flex-end', // Align content to bottom
          alignItems: 'center',
          minHeight: '64px',
          py: 0,
          pb: 1 // Small padding at bottom to prevent logo from touching the edge
        }}>
          <img 
            src="/logo-nb.png" 
            alt="Spenvo Logo" 
            style={{ 
              height: 24,
              marginBottom: 4 // Small margin to prevent touching the edge
            }}
          />
        </Toolbar>
      </AppBar>
      
      {/* Main content area with scrolling */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',
          mt: '64px', // Height of AppBar
          mb: '80px', // Height of BottomNavigation
          width: '100%',
          height: 'calc(100vh - 144px)', // Viewport height minus header and footer
          WebkitOverflowScrolling: 'touch',
          position: 'relative',
          zIndex: 1, // Lower than header and footer
          scrollbarWidth: 'none', // Hide scrollbar in Firefox
          '&::-webkit-scrollbar': {
            display: 'none' // Hide scrollbar in Chrome/Safari
          },
          msOverflowStyle: 'none', // Hide scrollbar in IE/Edge
        }}
      >
        <Container 
          component="main" 
          maxWidth="sm" 
          sx={{ 
            py: 2,
            px: { xs: 1, sm: 3 }
          }}
        >
          <Outlet />
        </Container>
      </Box>
      
      {/* Bottom navigation */}
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          zIndex: 1300,
          borderRadius: 0,
          boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.1)',
          width: '100%',
          transform: 'translateZ(0)', // Hardware acceleration
          willChange: 'transform', // Hint to browser for optimization
          backdropFilter: 'blur(8px)' // Ensures visibility even with content behind
        }} 
        elevation={3}
      >
        <BottomNavigation
          value={getActiveNavValue()}
          onChange={handleNavChange}
          showLabels
          sx={{ 
            height: 80,
            '& .MuiBottomNavigationAction-root': {
              paddingTop: 0.5, // Reduce top padding to move content higher
              minWidth: 'auto', // Allow icons to be closer
            }
          }}
        >
          <BottomNavigationAction label="Home" icon={<HomeIcon />} />
          <BottomNavigationAction label="Transactions" icon={<ReceiptIcon />} />
          <BottomNavigationAction label="Breakdown" icon={<PieChartIcon />} />
          <BottomNavigationAction label="Settings" icon={<SettingsIcon />} />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default Layout;